import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { FormsModule } from '@angular/forms';
import { SupplierService } from '../../features/suppliers/services/suppliers.service';
import { ToastrService } from 'ngx-toastr';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';
@Component({
  selector: 'app-suppliers',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
  ],
  templateUrl: './suppliers.component.html',
  styleUrl: './suppliers.component.scss',
})
export class SuppliersComponent implements OnInit {
  public viewPortSize: boolean = false;
  public loading: boolean = true;
  public error: any = null;
  readonly dialog = inject(MatDialog);
  columnDefs: any[] = [];
  private supplierService = inject(SupplierService);
  private toastr = inject(ToastrService);
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10];
  public themeClass: string = 'ag-theme-quartz';
  // public rowData: any[] | null = null;
  public rowData : any[] = [];

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  canCreateSuppliers$ = this.permissionService.hasPermission(
    ModuleIds.Suppliers,
    'Create'
  );
  canEditSuppliers$ = this.permissionService.hasPermission(
    ModuleIds.Suppliers,
    'Edit'
  );
  canDeleteSuppliers$ = this.permissionService.hasPermission(
    ModuleIds.Suppliers,
    'Delete'
  );
  canEditSuppliers: boolean = false;
  canDeleteSuppliers: boolean = false;

  constructor(private router: Router, private permissionService: PermissionService) {}

  ngOnInit(): void {
    this.checkViewportSize();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchSuppliers(searchTerm);
      });
    this.fetchSuppliers();
    this.canEditSuppliers$.subscribe(val => {
      this.canEditSuppliers = val;
    });
    this.canDeleteSuppliers$.subscribe(val => {
      this.canDeleteSuppliers = val;
    });
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.updateColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Supplier Name',
        field: 'wareHouseName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Address',
        field: 'address',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Supplier Code',
        field: 'code',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Contact',
        field: 'contact',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];

    this.columnDefs.push({
      field: 'action',
      headerName: 'Actions',
      floatingFilter: false,
      filter: false,
      flex: this.viewPortSize ? 1 : 0,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.gap = '20px';

        const editIcon = document.createElement('img');
        editIcon.src = 'assets/images/icons/icon-edit.svg';
        editIcon.style.cursor = 'pointer';
        editIcon.setAttribute('data-action', 'edit');
        if(this.canEditSuppliers){
          container.appendChild(editIcon);
        }

        const deleteIcon = document.createElement('img');
        deleteIcon.src = 'assets/images/icons/icon-delete.svg';
        deleteIcon.style.cursor = 'pointer';
        deleteIcon.setAttribute('data-action', 'delete');
        if(this.canDeleteSuppliers){
          container.appendChild(deleteIcon);
        }

        return container;
      },
    });
  }
  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onCellClicked(params: any) {
    const cellData = params.data;
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;
      console.log('action', action);

      if (action === 'edit') {
        this.router.navigate([`suppliers/supplier-edit/${cellData?.id}`]);
      } else if (action === 'delete') {
        this.onDelete(cellData);
      }
      // else {
      //   this.router.navigate([`cheque-list/cheque-details`], {
      //     queryParams: { id: cellData?.id },
      //   });
      // }
    }
    // else {
    //   this.router.navigate([`cheque-list/cheque-details`], {
    //     queryParams: { id: cellData?.id },
    //   });
    // }
  }

  onDelete(data: any) {
    const supplierID = data.id;
    if (!supplierID) return;
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete Supplier ?`,
        confirmText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.supplierService.deleteSupplier(supplierID).subscribe({
          next: () => {
            this.toastr.success('Supplier deleted successfully!', 'Success');
            this.fetchSuppliers();
          },
          error: (error) => {
            this.toastr.error(`Failed to delete Supplier`);
            console.error('Deletion error:', error);
          },
        });
      }
    });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  fetchSuppliers(search = '') {
    this.supplierService.fetchSuppliers(search).subscribe({
      next: (data) => {
        this.rowData = data.map((supplier: any) => ({
          id: supplier?.id,
          wareHouseName: supplier?.supplierName || '--',
          address: supplier?.address || '--',
          contact: supplier?.contact || '--',
          code: supplier?.supplierCode || '--',
        }));
        this.paginationPageSizeSelector = PaginationHelper.getPaginationPageSizeOptions(this.rowData);
      },
      error: (error) => {
        console.error('Error fetching suppliers:', error);
      },
    });
  }
}
